import {
  Box,
  Button,
  ButtonProps,
  Flex,
  Heading,
  Image,
  Text,
} from '@everlywell/ui-kit';
import BulletPointWithIcon, {
  BulletPointWithIconProps,
} from 'components/PageSections/components/BulletPointWithIcon';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import { createSharedButtonProps } from 'components/PageSections/utils';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, { MouseEvent } from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import useMdpModals from 'utils/hooks/useMdpModals';
import { ImageType } from 'utils/types';

export type BannerWithBulletPointsProps = {
  title?: string | null;
  titleColor?: string | null;
  description?: string | null;
  descriptionColor?: string | null;
  backgroundColor?: string | null;
  bulletPoints?: BulletPointWithIconProps[] | null;
  mobileImage?: ImageType | null;
  desktopImage?: ImageType | null;
  cta?: Queries.ContentfulCallToAction001 | null;
};

export default function BannerWithBulletPoints({
  title,
  titleColor,
  description,
  descriptionColor,
  backgroundColor,
  bulletPoints,
  mobileImage,
  desktopImage,
  cta,
}: BannerWithBulletPointsProps) {
  const { handleMdpCtaClick } = useMdpModals();

  const handleButtonClick = (clickEvent: MouseEvent<HTMLButtonElement>) => {
    handleMdpCtaClick({
      event: clickEvent,
      ctaUrl: cta?.url || '',
    });

    trackButtonClick();
  };

  const trackButtonClick = () => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        sectionHeader: title || 'No title provided',
        contentfulSection: 'Banner With Bullet Points',
        ctaText: cta?.label || 'No label provided',
      },
    });
  };

  const sharedButtonProps: ButtonProps = {
    variant: 'primary',
    onClick: handleButtonClick,
    id: `${title}-cta`,
    ...createSharedButtonProps(cta?.url || ''),
  };

  return (
    <SectionContainer size={['sm', null, null, 'xl']}>
      <Flex
        display="flex"
        alignItems="center"
        backgroundColor={backgroundColor || ''}
        flexDirection={['column', null, 'row-reverse']}
        borderRadius="xl"
        overflow="hidden"
      >
        <Flex flex={[1, null, 2]} justifyContent={[null, null, 'end']}>
          {/* Mobile Image */}
          {mobileImage ? (
            <Box display={[null, null, 'none']}>
              <Image
                as={GatsbyImage}
                image={mobileImage?.gatsbyImageData}
                alt={mobileImage?.description || ''}
              />
            </Box>
          ) : null}

          {/* Desktop Image */}
          {desktopImage ? (
            <Box display={['none', null, 'block']}>
              <Image
                as={GatsbyImage}
                image={desktopImage?.gatsbyImageData}
                alt={desktopImage?.description || ''}
              />
            </Box>
          ) : null}
        </Flex>
        <Box
          flex="1"
          marginTop={[8, null, 0]}
          paddingX={[6, null, 0]}
          paddingBottom={[6, null, 0]}
          marginLeft={[null, null, 12]}
        >
          {title ? (
            <Heading
              as="h2"
              size={['md', null, 'lg']}
              color={titleColor || ''}
              marginBottom={[2, null, 3]}
            >
              {title}
            </Heading>
          ) : null}

          {description ? (
            <Text color={descriptionColor || ''} fontSize={['md', null, 'lg']}>
              {description}
            </Text>
          ) : null}

          {bulletPoints ? (
            <Flex
              flexDirection="column"
              gap="2"
              marginTop="4"
              marginBottom={[6, null, 10]}
            >
              {bulletPoints.map((bullet, index) => (
                <BulletPointWithIcon key={index} {...bullet} />
              ))}
            </Flex>
          ) : null}

          {cta && cta?.label ? (
            <Button
              color={cta?.metadata?.theme?.textColor || 'tints.white'}
              backgroundColor={
                cta?.metadata?.theme?.backgroundColor || undefined
              }
              _hover={{
                backgroundColor: cta?.metadata?.theme?.gradientColor1 || 'none',
              }}
              {...sharedButtonProps}
              width={['100%', null, 'fit-content']}
            >
              {cta.label}
            </Button>
          ) : null}
        </Box>
      </Flex>
    </SectionContainer>
  );
}
